import React from 'react';
import { FileCheck, FileSignature, ClipboardList, Trophy, Send } from 'lucide-react';
import { cn } from '../lib/utils';

/* ------------------------------------------------------------------
   Loan Stage taxonomy — the 5 stages a deal moves through inside the
   broker's pipeline. The string keys live on `lead.loanStatus`.

   Legacy values from earlier iterations of this app
   (`submitted`, `conditional_approval`, etc.) are remapped to the
   nearest current stage so historical records still render cleanly.
   ------------------------------------------------------------------ */
export const LOAN_STAGES = [
  { key: 'ready_to_submit',         label: 'Ready to Submit',       Icon: ClipboardList,  tone: 'neutral' },
  { key: 'submitted_to_uw',         label: 'Submitted to UW',       Icon: Send,           tone: 'blue' },
  { key: 'approved_with_conditions', label: 'Approved w/ Conditions', Icon: FileCheck,    tone: 'purple' },
  { key: 'clear_to_close',          label: 'Clear to Close',        Icon: FileSignature,  tone: 'orange' },
  { key: 'funded',                  label: 'Funded',                Icon: Trophy,         tone: 'emerald' },
];

// Legacy → new stage mapping. Anything else is left as-is.
const LEGACY_REMAP = {
  // Old 4-stage taxonomy
  submitted: 'submitted_to_uw',
  conditionally_approved: 'approved_with_conditions',
  // Old 8-stage taxonomy (Feb 25 morning iteration)
  setup: 'ready_to_submit',
  application: 'submitted_to_uw',
  processing: 'submitted_to_uw',
  underwriting: 'submitted_to_uw',
  conditional_approval: 'approved_with_conditions',
  cd_docs_out: 'clear_to_close',
};

export const LOAN_STAGE_KEYS = LOAN_STAGES.map((s) => s.key);
const STAGE_BY_KEY = Object.fromEntries(LOAN_STAGES.map((s) => [s.key, s]));

export function normalizeStageKey(raw) {
  if (!raw) return null;
  return LEGACY_REMAP[raw] || raw;
}

export function getStage(rawKey) {
  const key = normalizeStageKey(rawKey);
  return key ? STAGE_BY_KEY[key] || null : null;
}

/* Tailwind palette per tone — kept inline so the component is fully
   self-describing and Tailwind's JIT picks up every class string. */
const TONE = {
  neutral:  { bg: 'bg-neutral-100',  text: 'text-neutral-700',  icon: 'text-neutral-500',  ring: 'ring-neutral-200' },
  blue:     { bg: 'bg-blue-50',      text: 'text-blue-700',     icon: 'text-blue-500',     ring: 'ring-blue-200' },
  indigo:   { bg: 'bg-indigo-50',    text: 'text-indigo-700',   icon: 'text-indigo-500',   ring: 'ring-indigo-200' },
  green:    { bg: 'bg-emerald-50',   text: 'text-emerald-700',  icon: 'text-emerald-500',  ring: 'ring-emerald-200' },
  orange:   { bg: 'bg-orange-50',    text: 'text-orange-700',   icon: 'text-orange-500',   ring: 'ring-orange-200' },
  purple:   { bg: 'bg-violet-50',    text: 'text-violet-700',   icon: 'text-violet-500',   ring: 'ring-violet-200' },
  teal:     { bg: 'bg-teal-50',      text: 'text-teal-700',     icon: 'text-teal-500',     ring: 'ring-teal-200' },
  emerald:  { bg: 'bg-emerald-50',   text: 'text-emerald-800',  icon: 'text-emerald-600',  ring: 'ring-emerald-300' },
};

/**
 * Colored pill that visually communicates a loan stage. Mirrors the
 * reference image: rounded pill, soft tinted background, matching icon
 * tinted to the same hue, no outline border (just a subtle 1px ring so
 * it reads cleanly against light or off-white surfaces).
 *
 *   size  : 'sm' (default) | 'md'
 *   key   : a `loanStatus` value — accepts legacy keys too
 */
export default function LoanStageBadge({ value, size = 'sm', uniform = false, className }) {
  const stage = getStage(value);
  // Uniform mode locks every pill to the same width so rows align cleanly
  // across the table. The label is allowed to truncate inside the pill if
  // it overflows (rare — only "Approved w/ Conditions" pushes the width).
  const uniformBase = uniform
    ? (size === 'md' ? 'w-[200px] justify-center' : 'w-[170px] justify-center')
    : '';
  if (!stage) {
    return (
      <span
        data-testid="loan-stage-badge-empty"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full text-[11px] font-semibold tracking-tight',
          'bg-neutral-50 text-neutral-400 ring-1 ring-neutral-200',
          size === 'md' ? 'px-3 py-1' : 'px-2.5 py-0.5',
          uniformBase,
          className,
        )}
      >
        —
      </span>
    );
  }
  const tone = TONE[stage.tone] || TONE.neutral;
  const Icon = stage.Icon;
  return (
    <span
      data-testid={`loan-stage-badge-${stage.key}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-semibold tracking-tight ring-1 whitespace-nowrap',
        tone.bg, tone.text, tone.ring,
        size === 'md' ? 'text-[13px] px-3.5 py-1.5' : 'text-[11.5px] px-2.5 py-1',
        uniformBase,
        className,
      )}
    >
      <Icon
        className={cn(tone.icon, size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5', 'shrink-0')}
        strokeWidth={2.2}
      />
      <span className="truncate">{stage.label}</span>
    </span>
  );
}
